import { useState, useEffect, useCallback, useRef } from 'react';
import { searchManualServiceCatalog } from '../services/subscriberWalletService';
import { mapMedicineFromAPI } from '../utils/medicineHelpers';

/**
 * Custom hook for medicine search with vendor filtering
 * Uses priority: vendor_id (priority 1) > order_id (priority 2) > service_provider (priority 3)
 * 
 * @param {string} currentRole - Current user role
 * @param {string|null} orderId - Order ID (optional, for priority 2)
 * @param {string|null} vendorId - Vendor ID from order detail (optional, for priority 1)
 * @param {string} serviceProvider - Service provider name (optional, for priority 3 fallback)
 * @param {string} selectedVendorId - Selected vendor ID from provider selection (fallback)
 */
export const useMedicineSearch = (currentRole, orderId = null, vendorId = null, serviceProvider = '', selectedVendorId = null) => {
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [hasSearched, setHasSearched] = useState(false); // Track if search has been executed
  const timeoutRef = useRef(null);
  const prevVendorRef = useRef({ orderId, vendorId, serviceProvider, selectedVendorId, searchText: '' });
  const searchInProgressRef = useRef(false);
  const prevSearchTextRef = useRef('');

  // Common search function
  const performSearch = useCallback(async (searchQuery = '') => {
    // Prevent duplicate API calls
    if (searchInProgressRef.current) {
      return;
    }

    if (!currentRole) {
      setMedicines([]);
      return;
    }

    // Determine which filter to use based on priority
    // Priority 1: vendor_id from order detail or selected vendor
    const hasVendorId = (vendorId && String(vendorId).trim()) || (selectedVendorId && String(selectedVendorId).trim());
    // Priority 2: order_id (backend gets vendor automatically)
    const hasOrderId = orderId && String(orderId).trim();
    // Priority 3: service_provider name (fallback)
    const hasServiceProvider = serviceProvider && String(serviceProvider).trim();

    // CRITICAL: Skip if no valid filter available - prevents showing all vendors' items
    if (!hasVendorId && !hasOrderId && !hasServiceProvider) {
      setMedicines([]);
      return;
    }

    searchInProgressRef.current = true;
      setLoading(true);
      setError(null);
      try {
        const params = {
          service_type: 'medicine'
        };

        // Priority 1: Use vendor_id if available (most accurate)
        if (hasVendorId) {
          params.vendor_id = vendorId || selectedVendorId;
        }
        // Priority 2: Use order_id (backend gets vendor automatically)
        else if (hasOrderId) {
          params.order_id = orderId;
        }
        // Priority 3: Fallback to service_provider name
        else if (hasServiceProvider) {
          params.service_provider = serviceProvider.trim();
        }

        // Double-check: Ensure at least one vendor filter is set before making API call
        if (!params.vendor_id && !params.order_id && !params.service_provider) {
          setMedicines([]);
          setLoading(false);
        searchInProgressRef.current = false;
          return;
        }

      if (searchQuery.trim()) {
        params.q = searchQuery.trim();
        }
        
        const response = await searchManualServiceCatalog(currentRole, params);
        if (response && response.metadata && response.metadata.api_status === true) {
          const medicinesList = response.msg?.items || [];
          const mappedMedicines = medicinesList.map(mapMedicineFromAPI);
          setMedicines(mappedMedicines);
          setHasSearched(true); // Mark that search has been executed
        } else {
          setError('Failed to load medicines');
          setMedicines([]);
          setHasSearched(true); // Mark that search has been executed (even if failed)
        }
      } catch (err) {
        setError('Failed to load medicines');
        setMedicines([]);
        setHasSearched(true); // Mark that search has been executed (even if error)
      } finally {
        setLoading(false);
      searchInProgressRef.current = false;
      }
  }, [currentRole, orderId, vendorId, serviceProvider, selectedVendorId]);

  // Track vendor/order/provider changes but DON'T trigger search automatically
  // Search will only happen when user types or presses Enter
  useEffect(() => {
    const prevVendor = prevVendorRef.current;
    const vendorChanged = 
      prevVendor.orderId !== orderId ||
      prevVendor.vendorId !== vendorId ||
      prevVendor.serviceProvider !== serviceProvider ||
      prevVendor.selectedVendorId !== selectedVendorId;

    if (vendorChanged) {
      // Clear any pending debounced search
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // Clear medicines list when vendor changes (don't show old results)
      setMedicines([]);
      setError(null);

      // Update ref to track vendor changes
      prevVendorRef.current = { orderId, vendorId, serviceProvider, selectedVendorId };
    }
  }, [orderId, vendorId, serviceProvider, selectedVendorId]);

  // Debounce search when only searchText changes (user typing)
  useEffect(() => {
    // Skip search if text is being cleared (e.g., after adding to cart)
    // Only search if user is actually typing, not when programmatically clearing
    const prevSearchText = prevSearchTextRef.current;
    const isClearing = prevSearchText.length > 0 && searchText.length === 0;
    
    // Update ref to track previous search text
    prevSearchTextRef.current = searchText;

    // If user is clearing the search (not typing), clear medicines and don't trigger new search
    // This prevents unnecessary API calls when clearing search text after adding to cart
    if (isClearing || searchText.length === 0) {
      // Clear medicines when search is cleared
      setMedicines([]);
      setHasSearched(false); // Reset search flag when text is cleared
      // Clear any pending debounced search
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    // Skip if searchText hasn't actually changed (initial render or same value)
    if (prevSearchText === searchText) {
      return;
    }

    // Clear any pending debounced search
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounce search for 8 seconds when user is typing
    timeoutRef.current = setTimeout(() => {
      performSearch(searchText);
    }, 8000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [searchText, performSearch]);

  // Function to trigger search immediately (for Enter key)
  const triggerSearch = useCallback(() => {
    // Clear any pending debounced search
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    // Trigger search immediately
    performSearch(searchText);
  }, [performSearch, searchText]);

  // Function to clear medicines list (useful after selection)
  const clearMedicines = useCallback(() => {
    setMedicines([]);
    setHasSearched(false); // Reset search flag when clearing
    // Clear any pending debounced search
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  return {
    medicines,
    loading,
    error,
    searchText,
    setSearchText,
    triggerSearch,
    clearMedicines,
    hasSearched
  };
};

